// src/views/pages/Leads.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { useLeadsController } from '../../controllers/useLeadsController';
import {
  CRM_STATUS_PIPELINE,
  CRM_SOURCES,
  CRM_REQUIREMENTS,
  CRM_CITIES
} from '../../services/leadService';
import ConfirmationModal from '../components/ConfirmationModal';
import { formatIndianCurrency } from '../../utils/currency';
import {
  Search,
  Download,
  Plus,
  Phone,
  MessageCircle,
  X,
  ChevronLeft,
  ChevronRight,
  Clock,
  Flame,
  Sun,
  Snowflake,
  CheckCircle,
  AlertCircle,
  Calendar,
  Building2,
  Send,
  Kanban,
  Shuffle,
  BarChart3
} from 'lucide-react';

const Leads = () => {
  const {
    loading,
    filteredLeads,
    paginatedLeads,
    totalLeads,
    totalPages,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    staffList,
    propertiesList,
    selectedLead,
    handleSelectLead,
    leadActivities,
    activitiesLoading,
    newNoteText,
    setNewNoteText,
    newNoteType,
    setNewNoteType,
    savingNote,
    handleAddNote,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    cityFilter,
    setCityFilter,
    sourceFilter,
    setSourceFilter,
    requirementFilter,
    setRequirementFilter,
    assignedStaffFilter,
    setAssignedStaffFilter,
    dateFilter,
    setDateFilter,
    showAddLeadModal,
    setShowAddLeadModal,
    newLeadForm,
    handleFormChange,
    duplicateWarning,
    checkingDuplicate,
    addingLead,
    formError,
    handleCreateLead,
    confirmOpen,
    setConfirmOpen,
    confirmTitle,
    confirmMessage,
    confirmType,
    executeConfirmAction,
    handleUpdateLeadStatus,
    handleUpdatePriority,
    handleAssignLead,
    handleExportCSV,
    currentUser,
    currentAdminProfile,
    callOutcome,
    setCallOutcome,
    callOutcomeNotes,
    setCallOutcomeNotes,
    callFollowupDate,
    setCallFollowupDate,
    savingCallOutcome,
    handleLogCallOutcome,
    roundRobinLoading,
    handleAutoAssignRoundRobin,
    callOutcomesList,
    forwardModalOpen,
    setForwardModalOpen,
    matchingProperties,
    matchingLoading,
    selectedPropertyIds,
    forwardingLoading,
    handleOpenForwardModal,
    handleTogglePropertySelect,
    handleConfirmForward
  } = useLeadsController();

  // Helper for status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'New':
        return <span className="badge badge-pending">New</span>;
      case 'Contacted':
        return <span className="badge badge-pending" style={{ backgroundColor: 'rgba(245, 158, 11, 0.15)', color: '#d97706' }}>Contacted</span>;
      case 'Interested':
        return <span className="badge badge-pending" style={{ backgroundColor: 'rgba(217, 119, 6, 0.12)', color: '#b45309' }}>Interested</span>;
      case 'Site Visit':
        return <span className="badge badge-pending" style={{ backgroundColor: 'var(--brand-accent-soft)', color: 'var(--brand-accent)' }}>Site Visit</span>;
      case 'Negotiation':
        return <span className="badge badge-pending" style={{ backgroundColor: 'rgba(14, 165, 233, 0.15)', color: '#0284c7' }}>Negotiation</span>;
      case 'Closed Won':
        return <span className="badge badge-approved">Closed Won</span>;
      case 'Closed Lost':
        return <span className="badge badge-disabled">Closed Lost</span>;
      default:
        return <span className="badge badge-pending">{status || 'New'}</span>;
    }
  };

  // Helper for source badge
  const getSourceBadge = (source) => {
    let bg = 'rgba(15, 23, 42, 0.08)';
    let color = 'var(--text-secondary)';
    if (source === 'popup') {
      bg = 'rgba(185, 28, 28, 0.12)';
      color = '#b91c1c';
    } else if (source === 'property enquiry') {
      bg = 'rgba(16, 185, 129, 0.12)';
      color = '#059669';
    } else if (source === 'app') {
      bg = 'var(--brand-accent-soft)';
      color = 'var(--brand-accent)';
    } else if (source === 'manual') {
      bg = 'rgba(217, 119, 6, 0.12)';
      color = '#d97706';
    }

    return (
      <span style={{
        fontSize: '11px',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        padding: '2px 6px',
        borderRadius: '4px',
        backgroundColor: bg,
        color: color,
        whiteSpace: 'nowrap'
      }}>
        {source || 'manual'}
      </span>
    );
  };

  // Helper for priority badge
  const getPriorityBadge = (priority) => {
    switch ((priority || '').toLowerCase()) {
      case 'hot':
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '11px', fontWeight: 700, padding: '2px 6px', borderRadius: '4px', backgroundColor: '#fee2e2', color: '#dc2626' }}>
            <Flame size={12} /> Hot
          </span>
        );
      case 'cold':
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '11px', fontWeight: 600, padding: '2px 6px', borderRadius: '4px', backgroundColor: '#e0f2fe', color: '#0284c7' }}>
            <Snowflake size={12} /> Cold
          </span>
        );
      default:
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '11px', fontWeight: 600, padding: '2px 6px', borderRadius: '4px', backgroundColor: '#fef3c7', color: '#d97706' }}>
            <Sun size={12} /> Warm
          </span>
        );
    }
  };

  return (
    <div className="page-body">
      {/* Top Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 className="page-title">Central Leads & CRM</h1>
          <p className="page-subtitle">Unified pipeline for website popup leads, property enquiries, mobile app users, and manual entries.</p>
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => setShowAddLeadModal(true)}
            className="btn btn-primary"
            style={{ gap: '6px', fontSize: '13px' }}
          >
            <Plus size={16} /> Add New Lead
          </button>

          {currentAdminProfile?.role !== 'Sales Executive' && (
            <button
              onClick={handleAutoAssignRoundRobin}
              disabled={roundRobinLoading}
              className="btn btn-secondary"
              style={{ gap: '6px', fontSize: '13px' }}
              title="Distribute unassigned leads equally among active sales executives"
            >
              <Shuffle size={15} /> {roundRobinLoading ? 'Assigning...' : 'Auto-Assign (Round-Robin)'}
            </button>
          )}

          <Link
            to="/crm"
            className="btn btn-secondary"
            style={{ gap: '6px', fontSize: '13px', textDecoration: 'none' }}
          >
            <Kanban size={15} /> Pipeline View
          </Link>

          <Link
            to="/crm/reports"
            className="btn btn-secondary"
            style={{ gap: '6px', fontSize: '13px', textDecoration: 'none' }}
          >
            <BarChart3 size={15} /> Reports & Performance
          </Link>

          <button
            onClick={handleExportCSV}
            className="btn btn-secondary"
            style={{ gap: '6px', fontSize: '13px' }}
          >
            <Download size={15} /> Export to CSV
          </button>
        </div>
      </div>

      {/* Sales Executive Role Notice */}
      {currentAdminProfile?.role === 'Sales Executive' && (
        <div style={{
          padding: '10px 16px',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          border: '1px solid rgba(59, 130, 246, 0.25)',
          borderRadius: 'var(--radius-sm)',
          marginBottom: '16px',
          fontSize: '12px',
          color: '#2563eb',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <AlertCircle size={16} />
          <span>You are logged in as <strong>Sales Executive</strong>. Role-based view active: showing leads assigned directly to you.</span>
        </div>
      )}

      {/* Filters Dashboard */}
      <div className="filters-bar" style={{ marginBottom: '20px' }}>
        {/* Search */}
        <div className="search-input-wrapper" style={{ flex: 2 }}>
          <Search size={16} className="search-icon-fixed" />
          <input
            type="text"
            placeholder="Search leads by customer name, phone, email, requirement, property, or city..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
        </div>

        {/* Status Pipeline Filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>Status:</span>
          <select
            className="filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Stages ({totalLeads})</option>
            {CRM_STATUS_PIPELINE.map(st => (
              <option key={st} value={st}>{st}</option>
            ))}
          </select>
        </div>

        {/* City Filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>City:</span>
          <select
            className="filter-select"
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
          >
            <option value="all">All Cities</option>
            {CRM_CITIES.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        {/* Source Filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>Source:</span>
          <select
            className="filter-select"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
          >
            <option value="all">All Sources</option>
            {CRM_SOURCES.map(src => (
              <option key={src} value={src}>{src}</option>
            ))}
          </select>
        </div>

        {/* Requirement Filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>Requirement:</span>
          <select
            className="filter-select"
            value={requirementFilter}
            onChange={(e) => setRequirementFilter(e.target.value)}
          >
            <option value="all">All Requirements</option>
            {CRM_REQUIREMENTS.map(req => (
              <option key={req} value={req}>{req}</option>
            ))}
          </select>
        </div>

        {/* Assigned Staff Filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>Staff:</span>
          <select
            className="filter-select"
            value={assignedStaffFilter}
            onChange={(e) => setAssignedStaffFilter(e.target.value)}
          >
            <option value="all">All Staff</option>
            <option value="mine">Assigned to Me</option>
            <option value="unassigned">Unassigned</option>
            {staffList.map(s => (
              <option key={s.id} value={s.id}>{s.full_name}</option>
            ))}
          </select>
        </div>

        {/* Date Filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>Date:</span>
          <select
            className="filter-select"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">Past 7 Days</option>
            <option value="month">Past 30 Days</option>
          </select>
        </div>
      </div>

      {/* Main Leads Table */}
      {loading ? (
        <div className="skeleton" style={{ height: '360px', borderRadius: 'var(--radius-md)' }} />
      ) : (
        <div className="table-container">
          <table className="premium-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Source</th>
                <th style={{ width: '22%' }}>Requirement</th>
                <th>City & Type</th>
                <th>Budget</th>
                <th>Priority</th>
                <th>Assigned Staff</th>
                <th>Pipeline Status</th>
                <th>Date</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedLeads.length === 0 ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
                    No leads match the specified filter.
                  </td>
                </tr>
              ) : (
                paginatedLeads.map((lead) => (
                  <tr
                    key={lead.id}
                    onClick={() => handleSelectLead(lead)}
                    style={{ cursor: 'pointer', transition: 'background-color var(--transition-fast)' }}
                  >
                    <td>
                      <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{lead.name}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                        <a
                          href={`tel:${lead.mobile}`}
                          onClick={(e) => e.stopPropagation()}
                          style={{ fontSize: '12px', color: 'var(--text-secondary)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '3px' }}
                        >
                          <Phone size={11} /> {lead.mobile}
                        </a>
                        {lead.mobile && (
                          <a
                            href={`https://wa.me/91${lead.mobile.replace(/\D/g, '')}`}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            style={{ color: '#25D366' }}
                            title="Chat on WhatsApp"
                          >
                            <MessageCircle size={13} />
                          </a>
                        )}
                      </div>
                      {lead.email && (
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '1px' }}>{lead.email}</div>
                      )}
                    </td>

                    <td>{getSourceBadge(lead.source)}</td>

                    <td>
                      <p style={{ fontSize: '12px', fontWeight: 600, margin: 0, color: 'var(--text-primary)' }}>
                        {lead.requirement}
                      </p>
                      {lead.property && (
                        <div style={{ fontSize: '11px', color: 'var(--primary)', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                          <Building2 size={11} /> {lead.property.title}
                        </div>
                      )}
                    </td>

                    <td>
                      <div style={{ fontWeight: 600, fontSize: '12px' }}>{lead.city || 'N/A'}</div>
                      {lead.property_type && (
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                          {lead.property_type}
                        </div>
                      )}
                    </td>

                    <td>
                      <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)' }}>
                        {lead.budget ? formatIndianCurrency(lead.budget) : 'Not specified'}
                      </span>
                    </td>

                    <td>{getPriorityBadge(lead.priority)}</td>

                    <td onClick={(e) => e.stopPropagation()}>
                      <select
                        value={lead.assigned_to || ''}
                        onChange={(e) => handleAssignLead(lead.id, e.target.value)}
                        style={{
                          padding: '4px 8px',
                          border: '1px solid var(--border-primary)',
                          borderRadius: '4px',
                          fontSize: '11px',
                          background: 'var(--bg-secondary)',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="">Unassigned</option>
                        {staffList.map(s => (
                          <option key={s.id} value={s.id}>{s.full_name}</option>
                        ))}
                      </select>
                    </td>

                    <td>
                      {getStatusBadge(lead.status)}
                      {lead.follow_up_date && (
                        <div style={{
                          fontSize: '10px',
                          marginTop: '4px',
                          color: new Date(lead.follow_up_date) < new Date() ? '#dc2626' : '#2563eb',
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '3px'
                        }}>
                          <Clock size={10} />
                          {new Date(lead.follow_up_date) < new Date() ? 'Overdue: ' : 'Follow-up: '}
                          {new Date(lead.follow_up_date).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                        </div>
                      )}
                    </td>

                    <td>
                      <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                        {new Date(lead.created_at).toLocaleDateString()}
                      </span>
                    </td>

                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => handleSelectLead(lead)}
                        className="btn btn-secondary btn-sm"
                        style={{ fontSize: '11px', padding: '4px 8px' }}
                      >
                        Manage Lead
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination Controls */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '14px 20px',
            borderTop: '1px solid var(--border-primary)',
            backgroundColor: 'var(--bg-secondary)',
            flexWrap: 'wrap',
            gap: '10px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: 'var(--text-secondary)' }}>
              <span>Rows per page:</span>
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                className="filter-select"
                style={{ padding: '3px 6px', fontSize: '11px' }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span>
                Showing {totalLeads === 0 ? 0 : (currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalLeads)} of {totalLeads} leads
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                className="btn btn-secondary btn-sm"
                style={{ padding: '4px 8px', opacity: currentPage === 1 ? 0.5 : 1 }}
              >
                <ChevronLeft size={14} /> Prev
              </button>

              <span style={{ fontSize: '12px', fontWeight: 600, padding: '0 8px' }}>
                Page {currentPage} of {totalPages}
              </span>

              <button
                type="button"
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                className="btn btn-secondary btn-sm"
                style={{ padding: '4px 8px', opacity: currentPage >= totalPages ? 0.5 : 1 }}
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= MODAL: ADD NEW MANUAL LEAD ================= */}
      {showAddLeadModal && (
        <div className="modal-backdrop">
          <div className="modal-content" style={{ maxWidth: '580px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h3 style={{ fontSize: '16px', fontWeight: 700, margin: 0 }}>Add New Lead (Manual Entry)</h3>
              <button
                type="button"
                onClick={() => setShowAddLeadModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleCreateLead}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {formError && (
                  <div style={{ backgroundColor: '#fee2e2', color: '#991b1b', padding: '8px 12px', borderRadius: '4px', fontSize: '12px' }}>
                    {formError}
                  </div>
                )}

                {/* Duplicate Mobile Warning Alert */}
                {duplicateWarning && (
                  <div style={{
                    backgroundColor: '#fef3c7',
                    border: '1px solid #f59e0b',
                    color: '#92400e',
                    padding: '10px 12px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px'
                  }}>
                    <AlertCircle size={16} style={{ marginTop: '2px', flexShrink: 0 }} />
                    <div>
                      <strong>Duplicate Lead Warning:</strong> A lead with mobile <strong>{duplicateWarning.mobile}</strong> already exists for <strong>{duplicateWarning.name}</strong> (Status: {duplicateWarning.status}).
                      <div style={{ marginTop: '4px' }}>
                        Created on {new Date(duplicateWarning.created_at).toLocaleDateString()} {duplicateWarning.assigned_to_name ? `• Assigned to ${duplicateWarning.assigned_to_name}` : ''}.
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="input-group">
                    <label className="input-label">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="e.g. Anand Singh"
                      value={newLeadForm.name}
                      onChange={handleFormChange}
                      className="input-field"
                    />
                  </div>

                  <div className="input-group">
                    <label className="input-label">
                      Mobile Number * {checkingDuplicate && <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>(Checking...)</span>}
                    </label>
                    <input
                      type="tel"
                      name="mobile"
                      required
                      maxLength={10}
                      placeholder="10-digit number"
                      value={newLeadForm.mobile}
                      onChange={handleFormChange}
                      className="input-field"
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="input-group">
                    <label className="input-label">Email Address (Optional)</label>
                    <input
                      type="email"
                      name="email"
                      placeholder="e.g. customer@example.com"
                      value={newLeadForm.email}
                      onChange={handleFormChange}
                      className="input-field"
                    />
                  </div>

                  <div className="input-group">
                    <label className="input-label">Source</label>
                    <select
                      name="source"
                      value={newLeadForm.source}
                      onChange={handleFormChange}
                      className="input-field"
                    >
                      <option value="manual">Manual Entry</option>
                      <option value="popup">Website Popup</option>
                      <option value="property enquiry">Property Enquiry</option>
                      <option value="app">Mobile App</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="input-group">
                    <label className="input-label">Requirement *</label>
                    <select
                      name="requirement"
                      value={newLeadForm.requirement}
                      onChange={handleFormChange}
                      className="input-field"
                    >
                      {CRM_REQUIREMENTS.map(req => (
                        <option key={req} value={req}>{req}</option>
                      ))}
                    </select>
                  </div>

                  <div className="input-group">
                    <label className="input-label">Select City *</label>
                    <select
                      name="city"
                      value={newLeadForm.city}
                      onChange={handleFormChange}
                      className="input-field"
                    >
                      {CRM_CITIES.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="input-group">
                    <label className="input-label">Property Type</label>
                    <select
                      name="property_type"
                      value={newLeadForm.property_type}
                      onChange={handleFormChange}
                      className="input-field"
                    >
                      <option value="Residential">Residential</option>
                      <option value="Commercial">Commercial</option>
                      <option value="Plot/Land">Plot/Land</option>
                    </select>
                  </div>

                  <div className="input-group">
                    <label className="input-label">Budget / Price Range</label>
                    <input
                      type="text"
                      name="budget"
                      placeholder="e.g. ₹25 - 40 Lakh"
                      value={newLeadForm.budget}
                      onChange={handleFormChange}
                      className="input-field"
                    />
                  </div>
                </div>

                {/* Link to specific property (optional) */}
                <div className="input-group">
                  <label className="input-label">Link to Property (Optional)</label>
                  <select
                    name="property_id"
                    value={newLeadForm.property_id}
                    onChange={handleFormChange}
                    className="input-field"
                  >
                    <option value="">None / General Inquiry</option>
                    {propertiesList.map(p => (
                      <option key={p.id} value={p.id}>{p.title} ({p.city})</option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="input-group">
                    <label className="input-label">Assign To Staff</label>
                    <select
                      name="assigned_to"
                      value={newLeadForm.assigned_to}
                      onChange={handleFormChange}
                      className="input-field"
                    >
                      <option value="">Unassigned</option>
                      {staffList.map(s => (
                        <option key={s.id} value={s.id}>{s.full_name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="input-group">
                    <label className="input-label">Lead Priority</label>
                    <select
                      name="priority"
                      value={newLeadForm.priority}
                      onChange={handleFormChange}
                      className="input-field"
                    >
                      <option value="Hot">Hot 🔥</option>
                      <option value="Warm">Warm ☀️</option>
                      <option value="Cold">Cold ❄️</option>
                    </select>
                  </div>
                </div>

                <div className="input-group">
                  <label className="input-label">Initial Notes / Requirements</label>
                  <textarea
                    rows={2}
                    name="notes"
                    placeholder="Customer requirements, preferred locality, notes from call..."
                    value={newLeadForm.notes}
                    onChange={handleFormChange}
                    className="input-field"
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setShowAddLeadModal(false)}
                  className="btn btn-secondary btn-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addingLead}
                  className="btn btn-primary btn-sm"
                >
                  {addingLead ? 'Saving...' : 'Create Lead'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ================= LEAD DETAIL DRAWER (POORI INFO & TIMELINE) ================= */}
      {selectedLead && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.45)',
          zIndex: 100,
          display: 'flex',
          justifyContent: 'flex-end',
          backdropFilter: 'blur(2px)'
        }}>
          <div style={{
            width: '100%',
            maxWidth: '560px',
            backgroundColor: 'var(--bg-primary)',
            height: '100%',
            overflowY: 'auto',
            boxShadow: 'var(--shadow-2xl)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Drawer Header */}
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid var(--border-primary)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: 'var(--bg-secondary)'
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <h2 style={{ fontSize: '17px', fontWeight: 700, margin: 0, color: 'var(--text-primary)' }}>
                    {selectedLead.name}
                  </h2>
                  {getPriorityBadge(selectedLead.priority)}
                </div>
                <div style={{ marginTop: '4px', display: 'flex', gap: '6px', alignItems: 'center' }}>
                  {getSourceBadge(selectedLead.source)}
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                    ID: {selectedLead.id.slice(0, 8)}...
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => handleSelectLead(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Quick Contact Bar */}
            <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border-primary)', display: 'flex', gap: '10px' }}>
              <a
                href={`tel:${selectedLead.mobile}`}
                className="btn btn-secondary btn-sm"
                style={{ flex: 1, textDecoration: 'none', justifyContent: 'center' }}
              >
                <Phone size={14} /> Call ({selectedLead.mobile})
              </a>
              {selectedLead.mobile && (
                <a
                  href={`https://wa.me/91${selectedLead.mobile.replace(/\D/g, '')}?text=${encodeURIComponent(`Hi ${selectedLead.name}, thank you for contacting NOBROKERAGE regarding ${selectedLead.requirement}.`)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-sm"
                  style={{ flex: 1, backgroundColor: '#25D366', color: '#fff', textDecoration: 'none', justifyContent: 'center', border: 'none' }}
                >
                  <MessageCircle size={14} /> WhatsApp
                </a>
              )}
            </div>

            {/* Drawer Body */}
            <div style={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Visual Pipeline Stage Stepper */}
              <div>
                <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>
                  Pipeline Stage Tracker
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {CRM_STATUS_PIPELINE.map(st => {
                    const isCurrent = (selectedLead.status || 'New') === st;
                    return (
                      <button
                        key={st}
                        type="button"
                        onClick={() => handleUpdateLeadStatus(selectedLead.id, st)}
                        style={{
                          fontSize: '11px',
                          fontWeight: isCurrent ? 700 : 500,
                          padding: '4px 10px',
                          borderRadius: '999px',
                          border: isCurrent ? '2px solid var(--primary)' : '1px solid var(--border-primary)',
                          backgroundColor: isCurrent ? 'var(--primary)' : 'var(--bg-secondary)',
                          color: isCurrent ? '#fff' : 'var(--text-secondary)',
                          cursor: 'pointer',
                          transition: 'all var(--transition-fast)'
                        }}
                      >
                        {st}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Lead Details Card */}
              <div style={{
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border-primary)',
                padding: '14px'
              }}>
                <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>
                  Customer & Requirement Details
                </div>
                <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 6px 0' }}>
                  {selectedLead.requirement}
                </p>
                {selectedLead.notes && (
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic', margin: '0 0 10px 0', lineHeight: '1.4' }}>
                    "{selectedLead.notes}"
                  </p>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px', borderTop: '1px solid var(--border-primary)', paddingTop: '10px' }}>
                  <div>
                    <span style={{ color: 'var(--text-muted)' }}>City:</span> <strong>{selectedLead.city || 'N/A'}</strong>
                  </div>
                  <div>
                    <span style={{ color: 'var(--text-muted)' }}>Type:</span> <strong>{selectedLead.property_type || 'N/A'}</strong>
                  </div>
                  <div>
                    <span style={{ color: 'var(--text-muted)' }}>Budget:</span> <strong>{selectedLead.budget ? formatIndianCurrency(selectedLead.budget) : 'N/A'}</strong>
                  </div>
                  <div>
                    <span style={{ color: 'var(--text-muted)' }}>Email:</span> {selectedLead.email || 'N/A'}
                  </div>
                  {selectedLead.property && (
                    <div style={{ gridColumn: 'span 2' }}>
                      <span style={{ color: 'var(--text-muted)' }}>Linked Property:</span>{' '}
                      <Link to={`/properties/${selectedLead.property.id}`} style={{ color: 'var(--primary)', fontWeight: 600 }}>
                        {selectedLead.property.title} ({selectedLead.property.city})
                      </Link>
                    </div>
                  )}
                  <div>
                    <span style={{ color: 'var(--text-muted)' }}>Created:</span> {new Date(selectedLead.created_at).toLocaleString()}
                  </div>
                  {selectedLead.follow_up_date && (
                    <div style={{
                      gridColumn: 'span 2',
                      padding: '8px 12px',
                      borderRadius: '4px',
                      backgroundColor: new Date(selectedLead.follow_up_date) < new Date() ? 'rgba(239, 68, 68, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                      color: new Date(selectedLead.follow_up_date) < new Date() ? '#dc2626' : '#2563eb',
                      fontSize: '11px',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <Clock size={13} />
                      <span>Next Follow-up Scheduled: {new Date(selectedLead.follow_up_date).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Rule 4: Forward generic Buy/Rent lead to matching property owners */}
              {!selectedLead.property_id && (
                <div style={{
                  backgroundColor: 'var(--bg-secondary)',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border-primary)',
                  padding: '14px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-primary)' }}>
                      Forward to Matching Property Owner(s)
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                      Suggest matching verified properties in {selectedLead.city || 'target city'} and forward this lead.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleOpenForwardModal}
                    className="btn btn-primary btn-sm"
                    style={{ gap: '4px', flexShrink: 0 }}
                  >
                    <Send size={13} /> Forward
                  </button>
                </div>
              )}

              {/* Assignment & Priority Controls */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '4px' }}>
                    Assigned Staff
                  </label>
                  <select
                    className="filter-select"
                    style={{ width: '100%' }}
                    value={selectedLead.assigned_to || ''}
                    onChange={(e) => handleAssignLead(selectedLead.id, e.target.value)}
                  >
                    <option value="">Unassigned</option>
                    {staffList.map(s => (
                      <option key={s.id} value={s.id}>{s.full_name} ({s.role})</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '4px' }}>
                    Lead Priority
                  </label>
                  <select
                    className="filter-select"
                    style={{ width: '100%' }}
                    value={selectedLead.priority || 'Warm'}
                    onChange={(e) => handleUpdatePriority(selectedLead.id, e.target.value)}
                  >
                    <option value="Hot">Hot 🔥</option>
                    <option value="Warm">Warm ☀️</option>
                    <option value="Cold">Cold ❄️</option>
                  </select>
                </div>
              </div>

              {/* Call Outcome Logger & Next Follow-up (Phase 2) */}
              <div style={{
                borderTop: '1px solid var(--border-primary)',
                paddingTop: '16px',
                backgroundColor: 'var(--bg-secondary)',
                padding: '14px',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border-primary)'
              }}>
                <div style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-primary)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Phone size={14} color="var(--primary)" /> Log Call Outcome & Stage Update
                </div>
                <form onSubmit={handleLogCallOutcome}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>
                        Call Outcome
                      </label>
                      <select
                        className="filter-select"
                        style={{ width: '100%', fontSize: '12px' }}
                        value={callOutcome}
                        onChange={(e) => setCallOutcome(e.target.value)}
                      >
                        {callOutcomesList?.map(o => (
                          <option key={o.label} value={o.label}>
                            {o.label} (→ {o.nextStatus})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>
                        Next Follow-up Date/Time
                      </label>
                      <input
                        type="datetime-local"
                        className="input-field"
                        style={{ fontSize: '11px', padding: '6px 8px', width: '100%' }}
                        value={callFollowupDate}
                        onChange={(e) => setCallFollowupDate(e.target.value)}
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>
                      Call Notes / Discussion Summary
                    </label>
                    <textarea
                      rows={2}
                      placeholder="Summary of customer conversation, pricing discussed, next actions..."
                      value={callOutcomeNotes}
                      onChange={(e) => setCallOutcomeNotes(e.target.value)}
                      className="input-field"
                      style={{ width: '100%', fontSize: '12px' }}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={savingCallOutcome}
                    className="btn btn-primary btn-sm"
                    style={{ width: '100%', gap: '6px' }}
                  >
                    <CheckCircle size={14} /> {savingCallOutcome ? 'Logging Call...' : 'Log Call & Move Stage'}
                  </button>
                </form>
              </div>

              {/* Add Activity Note Form */}
              <div style={{ borderTop: '1px solid var(--border-primary)', paddingTop: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>
                  Log Call / Add Activity Note
                </div>
                <form onSubmit={handleAddNote}>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <select
                      className="filter-select"
                      value={newNoteType}
                      onChange={(e) => setNewNoteType(e.target.value)}
                      style={{ fontSize: '11px', width: '140px' }}
                    >
                      <option value="call">Phone Call</option>
                      <option value="whatsapp">WhatsApp</option>
                      <option value="site_visit">Site Visit</option>
                      <option value="note">Internal Note</option>
                    </select>
                  </div>

                  <textarea
                    rows={2}
                    placeholder="Enter call outcome, customer interest, or notes..."
                    value={newNoteText}
                    onChange={(e) => setNewNoteText(e.target.value)}
                    className="input-field"
                    style={{ width: '100%', marginBottom: '8px', fontSize: '12px' }}
                  />

                  <button
                    type="submit"
                    disabled={savingNote || !newNoteText.trim()}
                    className="btn btn-primary btn-sm"
                    style={{ width: '100%', gap: '6px' }}
                  >
                    <Send size={13} /> {savingNote ? 'Saving...' : 'Add Note to Timeline'}
                  </button>
                </form>
              </div>

              {/* Activity History Timeline ("Kisne Kab Kya Badla") */}
              <div style={{ borderTop: '1px solid var(--border-primary)', paddingTop: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '10px' }}>
                  Activity History ("Kisne Kab Kya Badla")
                </div>

                {activitiesLoading ? (
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Loading history...</p>
                ) : leadActivities.length === 0 ? (
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>No activities recorded yet.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {leadActivities.map(act => (
                      <div
                        key={act.id}
                        style={{
                          backgroundColor: 'var(--bg-secondary)',
                          borderRadius: 'var(--radius-sm)',
                          padding: '10px 12px',
                          borderLeft: '3px solid var(--primary)'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                          <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--primary)' }}>
                            {act.activity_type.replace('_', ' ')}
                          </span>
                          <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                            {new Date(act.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                          </span>
                        </div>
                        <p style={{ fontSize: '12px', margin: '0 0 4px 0', color: 'var(--text-primary)', lineHeight: '1.4' }}>
                          {act.notes}
                        </p>
                        <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                          Action by: <strong>{act.performer_name || 'Staff'}</strong>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Forward to Property Owners Modal (Rule 4) */}
      {forwardModalOpen && selectedLead && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 150,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
          backdropFilter: 'blur(3px)'
        }}>
          <div style={{
            backgroundColor: 'var(--bg-primary)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-2xl)',
            width: '100%',
            maxWidth: '680px',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid var(--border-primary)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: 'var(--bg-secondary)'
            }}>
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: 700, margin: 0, color: 'var(--text-primary)' }}>
                  Forward Lead to Matching Property Owners
                </h3>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                  Buyer: {selectedLead.name} ({selectedLead.requirement} in {selectedLead.city})
                </span>
              </div>
              <button
                type="button"
                onClick={() => setForwardModalOpen(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
              {matchingLoading ? (
                <div style={{ textAlign: 'center', padding: '30px' }}>
                  <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Searching matching verified properties...</p>
                </div>
              ) : matchingProperties.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px' }}>
                  <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
                    No matching active properties found in {selectedLead.city || 'this city'}.
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                    Select one or more matching property owners below to forward this lead:
                  </div>
                  {matchingProperties.map((prop) => {
                    const isSelected = selectedPropertyIds.includes(prop.id);
                    const owner = prop.owner || {};
                    return (
                      <div
                        key={prop.id}
                        onClick={() => handleTogglePropertySelect(prop.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px 14px',
                          borderRadius: '8px',
                          border: isSelected ? '2px solid var(--primary)' : '1px solid var(--border-primary)',
                          backgroundColor: isSelected ? 'var(--bg-tertiary)' : 'var(--bg-secondary)',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          style={{ accentColor: 'var(--primary)', cursor: 'pointer' }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>
                              {prop.title}
                            </span>
                            <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--primary)' }}>
                              ₹{prop.price?.toLocaleString('en-IN') || 'Price on request'}
                            </span>
                          </div>
                          <div style={{ display: 'flex', gap: '16px', fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                            <span>📍 {prop.city}</span>
                            <span>🏠 {prop.property_type}</span>
                            <span>👤 Owner: <strong>{owner.full_name || 'Listing Owner'}</strong> ({owner.phone_number || 'No phone'})</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div style={{
              padding: '14px 20px',
              borderTop: '1px solid var(--border-primary)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: 'var(--bg-secondary)'
            }}>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                {selectedPropertyIds.length} property owner(s) selected
              </span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => setForwardModalOpen(false)}
                  className="btn btn-secondary btn-sm"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmForward}
                  disabled={forwardingLoading || selectedPropertyIds.length === 0}
                  className="btn btn-primary btn-sm"
                  style={{ gap: '6px' }}
                >
                  <Send size={13} /> {forwardingLoading ? 'Forwarding...' : `Forward to ${selectedPropertyIds.length} Owner(s)`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={executeConfirmAction}
        title={confirmTitle}
        message={confirmMessage}
        type={confirmType}
        isLoading={loading}
        confirmText="Proceed"
      />
    </div>
  );
};

export default Leads;
